import { Link } from "react-router-dom";
import { BsFileEarmarkPdf, BsTelephone } from "react-icons/bs";
import { CardComp, ButtonComp } from "../..";
import { InvestmentDetailStickySummary } from "../InvestmentDetailSummary";
import { investorButtonProps } from "../investorButtonStyles";

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function RailCard({ title, children, className = "" }) {
  return (
    <CardComp cardClassName={`border-0 shadow-sm rounded-4 ${className}`}>
      {title ? (
        <h6 className="fw-semibold mb-2" style={{ color: "#383838", fontSize: "0.95rem" }}>
          {title}
        </h6>
      ) : null}
      {children}
    </CardComp>
  );
}

export default function InvestmentDetailSidebarRail({
  opportunity,
  onInvest,
  selectedUnits,
  onUnitsChange,
  canInvest = true,
  unavailableNote = "",
}) {
  if (!opportunity) return null;

  const support = opportunity.support || opportunity.supportContact || {};
  const hasSupportContact = Boolean(support.phone || support.email || support.whatsapp);
  const topDocuments = (opportunity.documents || []).filter((doc) => Boolean(doc?.url)).slice(0, 2);

  return (
    <>
      <InvestmentDetailStickySummary
        opportunity={opportunity}
        onInvest={onInvest}
        selectedUnits={selectedUnits}
        onUnitsChange={onUnitsChange}
        className=""
        embedded
        canInvest={canInvest}
        unavailableNote={unavailableNote}
      />

      {hasSupportContact ? (
        <RailCard title="Contact an advisor">
          <p className="text-muted small mb-3" style={{ lineHeight: 1.55 }}>
            Speak with our team before you commit. We can walk you through terms and allocation.
          </p>
          <ButtonComp
            btnText="Talk to an advisor"
            onClick={() => scrollToSection("support")}
            {...investorButtonProps.secondary}
            ButtonInlineStyle={{
              ...investorButtonProps.secondary.ButtonInlineStyle,
              minHeight: 42,
            }}
          />
        </RailCard>
      ) : null}

      {topDocuments.length > 0 ? (
        <RailCard title="Key documents">
          <ul className="list-unstyled mb-3 small">
            {topDocuments.map((doc) => (
              <li key={doc.id} className="d-flex align-items-start gap-2 mb-2">
                <BsFileEarmarkPdf
                  className="flex-shrink-0 mt-1"
                  style={{ color: "var(--color-primary)" }}
                  aria-hidden
                />
                <button
                  type="button"
                  className="btn btn-link p-0 text-start text-decoration-none"
                  style={{ color: "#45464e", fontSize: "inherit" }}
                  onClick={() => window.open(doc.url, "_blank", "noopener,noreferrer")}
                >
                  {doc.title}
                </button>
              </li>
            ))}
          </ul>
          <ButtonComp
            btnText="View all documents"
            onClick={() => scrollToSection("documents")}
            {...investorButtonProps.outline}
            ButtonInlineStyle={{
              ...investorButtonProps.outline.ButtonInlineStyle,
              minHeight: 40,
              fontSize: "0.85rem",
              width: "100%",
              minWidth: 0,
            }}
          />
        </RailCard>
      ) : null}

      {hasSupportContact ? (
        <RailCard title="Need help?">
          {support.phone ? (
            <p className="text-muted small mb-2">
              <BsTelephone className="me-1" aria-hidden />
              {support.phone}
            </p>
          ) : null}
          {support.email ? (
            <p className={`text-muted small text-truncate ${support.phone ? "mb-3" : "mb-3"}`}>
              {support.email}
            </p>
          ) : (
            <div className="mb-3" />
          )}
          <div className="d-flex flex-column gap-2">
            <ButtonComp
              btnText="Get support"
              onClick={() => scrollToSection("support")}
              {...investorButtonProps.outline}
              ButtonInlineStyle={{
                ...investorButtonProps.outline.ButtonInlineStyle,
                minHeight: 40,
                width: "100%",
                minWidth: 0,
              }}
            />
            {support.phone ? (
              <ButtonComp
                btnText="Request callback"
                onClick={() => {
                  window.location.href = `tel:${support.phone}`;
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  minHeight: 40,
                  width: "100%",
                  minWidth: 0,
                }}
              />
            ) : null}
          </div>
        </RailCard>
      ) : null}

      <RailCard title="Investor resources" className="mb-0">
        <p className="text-muted small mb-2" style={{ lineHeight: 1.55 }}>
          New to investing with us? Review how requests, payments, and returns work.
        </p>
        <Link
          to="/invest/investments"
          className="small fw-semibold"
          style={{ color: "var(--color-secondary)" }}
        >
          Browse more opportunities →
        </Link>
      </RailCard>
    </>
  );
}
